export const meta = {
  id: '45-verbal-prefixes-and-suffixes',
  order: 45,
  titleFi: 'Verbin etuliitteet ja johtimet',
  titleEn: 'Verbal prefixes and suffixes',
  lemmaIds: [
    'henujagon',
    'henumāzigon',
    'upālegon',
    'gōvilagon',
    'bēuvāedagon',
    'imāzigon',
    'sytivīlībagon',
    'sytisindigon',
    'ivestragon',
    'iepagon',
    'jorepagon',
    'joheghagon',
    'mīvindigon',
    'mazverdagon',
    'mazemagon',
    'mazōregon',
    'nājikagon',
    'nātojagon',
    'qringaomagon',
    'qrimmajēbagon',
    'qrindārligon',
    'ozūndegon',
    'ozudligon',
    'ohīlagon',
    'lykemagon',
    'vōkemagon',
    'qubemagon',
    'kiremagon',
    'ademmagon',
    'ōdrikagon',
    'ropakagon',
    'jikagon',
    'kustikagon',
    'hinikagon',
    'umbikagon',
    'gierūljagon',
    'lōtūljagon',
    'ūbriljagon',
    'obūljagon',
    'giēñagon',
    'arliñagon',
    'morghākogon',
    'belmuragon',
    'onduragon',
    'rytsuragon',
    'ūndegon',
    'ryptegon',
    'gōntegon',
    'verdligon',
    'ours:udōñe',
    'ours:epēbagon',
  ],
  exampleIds: [
    'ex-43ac60369399',
    'hotd-s3e05-009',
    'got-s7e04-001',
    'ex-1ac36741e3f2',
    'ex-17fb52515c32',
    'got-s4e03-003',
    'got-s3e05-009',
    'ex-5a505dfcead9',
    'got-s8e06-005',
    'hotd-s3e08-003',
    'ex-62e752867160',
    'hotd-s3e03-008',
    'got-s6e08-001',
    'hotd-s3e04-001',
    'got-s5e03-008',
    'got-s8e06-004',
    'got-s3e04-009',
    'got-s4e03-002',
    'hotd-s1e02-003',
    'hotd-s1e08-002',
    'hotd-s1e02-005',
    'hotd-s1e04-009',
    'got-s6e01-005',
    'got-s4e10-004',
    'hotd-s1e10-002',
    'hotd-s3e03-013',
    'got-s3e06-003',
    'got-s5e03-006',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Verbal_Prefixes',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Derivational_Affixes',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes',
    'https://wiki.languageinvention.com/index.php?title=Upālegon',
    'https://wiki.languageinvention.com/index.php?title=Henumāzigon',
    'https://wiki.languageinvention.com/index.php?title=-ākogon',
    'https://wiki.languageinvention.com/index.php?title=Rytsuragon',
    'https://wiki.languageinvention.com/index.php?title=Lōtūljagon',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammar: ['prefixes', 'suffixes'],
  grammarAlso: ['paateopas'],
  showLines: ['hotd-s3e05-009', 'got-s7e04-001', 'got-s4e03-003', 'got-s3e05-009', 'got-s8e06-005', 'hotd-s3e08-003', 'hotd-s3e03-008', 'got-s6e08-001', 'hotd-s3e04-001', 'got-s5e03-008', 'got-s8e06-004', 'got-s3e04-009', 'got-s4e03-002', 'hotd-s1e02-003', 'hotd-s1e08-002', 'hotd-s1e02-005', 'hotd-s1e04-009', 'got-s6e01-005', 'got-s4e10-004', 'hotd-s1e10-002', 'hotd-s3e03-013', 'got-s3e06-003', 'got-s5e03-006'],
  pronunciation: { focus: 'stress', words: ['henujagon', 'henumāzigon', 'upālegon', 'gōvilagon', 'bēuvāedagon'], lines: ['got-s7e04-001', 'got-s4e03-003', 'got-s3e05-009'] },
  letter: {
    fi: 'Johda kolme verbiä tämän viikon etuliitteillä tai johtimilla ja käytä niitä lauseissa.',
    en: 'Derive three verbs with this week’s prefixes or suffixes and use them in sentences.',
  },
  scene: 'hotd-s3e03-1',
}

# Verbin etuliitteet ja johtimet

Kaksi tapaa tehdä verbistä uusi verbi: etuliite eteen tai johdin perään. Molemmista on apua ennen kaikkea lukiessa.

## Verbin etuliitteet: mistä, minne, kenen puolesta

Duolingon Valenssi-osio (<LessonLink n={33}>oppitunti 33</LessonLink>) paljasti, että moni sanakirjan verbi alkaa etuliitteellä: *a-/h-/s-/z-*
(välinepassiivi), *u-/v-* (lokatiivinen applikatiivi) ja *i-/j-* (obliikvinen
applikatiivi). Osio mainitsi myös, että applikatiivien eteen voi liittää adpositioita. Tällä
oppitunnilla tutustutaan näihin adpositioetuliitteisiin sekä joukkoon muita etuliitteitä, joita
sanakirjassa on runsaasti. Kun tunnistat ne, voit arvata monen pitkän verbin merkityksen ja rakentaa
omiin kirjeisiin täsmällisempiä verbejä.

## Etuliitteiden järjestys

Wikin etuliitesivu (*Verbal Prefixes*) jakaa etuliitteet kolmeen kerrokseen juuresta ulospäin:

1. **jatkuvuuden** *jo(r)-* lähinnä juurta,
2. **valenssin** etuliitteet (*a-*, *i-*, *u-*) sen ulkopuolella – vain yksi kerrallaan,
3. **adpositioetuliitteet** (*hen-, bē-, gō-, va-, syt-*) uloimpina.

Esimerkiksi <Hv glossed>ajomemēbagon</Hv> ’raivota edelleen’ = *a-* + *jo-* + <Hv glossed>memēbagon</Hv> ’marssia’,
<Hv glossed>sytivīlībagon</Hv> ’taistella jonkun puolesta’ = *syt-* + *i-* + <Hv>vīlībagon</Hv>,
<Hv glossed>henujagon</Hv> ’lähteä’ = *hen-* + *u-* + <Hv>jagon</Hv>.

<Compare fi="ulos|mitata" hv="hen|ujagon" label="adpositio etuliitteenä" />

Suomessakin adverbi voi kiinnittyä verbiin (*ulosmitata, sisäänkirjautua*), mutta valyriassa se on
paljon tavallisempaa ja kieliopillisesti säännellympää.

## Lokatiiviset adpositioetuliitteet

Nämä esiintyvät aina lokatiivisen applikatiivin kanssa (*u-*, jonka muoto vaihtelee: *u-, v-, b-, o-*):

<div className="lesson-table-wrap" tabIndex={0}>
<table className="lesson-table">
  <thead><tr><th>Etuliite</th><th>Merkitys</th><th>Verbejä</th></tr></thead>
  <tbody>
    <tr><td><Hv>hen-</Hv></td><td>-sta, pois</td><td><Hv glossed>henujagon</Hv> ’lähteä’, <Hv glossed>henumāzigon</Hv> ’tulla jostakin’</td></tr>
    <tr><td><Hv>bē-</Hv></td><td>päällä, -sta (aiheesta)</td><td><Hv glossed>bēvilagon</Hv> ’olla jonkun velvollisuus’, <Hv glossed>bēuvāedagon</Hv> ’laulaa jostakin’</td></tr>
    <tr><td><Hv>gō-</Hv></td><td>alla</td><td><Hv glossed>gōvilagon</Hv> ’olla jonkin alla’</td></tr>
    <tr><td><Hv>va-</Hv></td><td>kohti</td><td><Hv glossed>vaoresagon</Hv> ’suosia’ (’kallistua kohti’), <Hv glossed>vaodekuragon</Hv> ’lähestyä’</td></tr>
  </tbody>
</table>
</div>

Wikin mukaan applikatiivin muodot jakautuvat todennäköisesti näin: *u-* konsonantin edellä ja
konsonanttien välissä (<Hv>henujagon</Hv>), *v-* vokaalien välissä (<Hv>bēvilagon</Hv>), *b-*
konsonanttiin päättyvän etuliitteen ja *i*:n tai *u*:n välissä (<Hv>hembistas</Hv> < *hen-b-istas*)
ja *o-* *a*:han päättyvän etuliitteen jälkeen (<Hv>vaoresagon</Hv>).

Wikin esimerkkinä on Duolingosta tuttu <Hv glossed>bēvilagon</Hv>: kirjaimellisesti ’maata jonkun
päällä’, ja siitä merkitys ’olla jonkun vastuulla, täytyä’. Daenerysin tuutulaulussa taas on
<Hv glossed>munnon hembistas</Hv> ’suru on paennut’, jossa on <Hv>henujagon</Hv>-verbin perfekti.

<Line id="got-s7e04-001" />

### Kohde heti verbin edessä

Lokatiivisen applikatiivin objekti tulee **välittömästi verbin eteen**, ja se on datiivissa – tai
genetiivissä, jos se on yksikössä. Wiki ja sanakirja eivät ole tässä aivan samaa mieltä (ks.
huomautus alla). Ennustuksen sanoin:

<Example id="ex-43ac60369399" />

<Interlinear hv="Kīviō dārilaros ñurho ānogro henumāz|is." gloss="lupaus-GEN prinssi-NOM minun-AQ.GEN veri-GEN tulla.jostakin-PRS.3SG" fi="Minun verestäni tulee luvattu prinssi." />

<Hv glossed>Ānogro</Hv> ’verestä’ on genetiivissä ja heti verbin edessä. Samoin wikin esimerkissä,
jossa on *va*-verbi:

<Example id="ex-1ac36741e3f2" />

<Hv glossed>Luo</Hv> (<Term id="relatiivipronomini">relatiivipronominin</Term> <Hv glossed>lȳ</Hv> ’se joka’ genetiivi; relatiivilauseista lisää <LessonLink n={46}>oppitunnilla 46</LessonLink>) on suoraan verbin <Hv glossed>vaoresan</Hv> ’suosin’ edessä.

<Line id="hotd-s3e05-009" />

<Interlinear hv="Sepār skoro syt jaehossa yno upāl|esi?" gloss="niin.sitten mikä-GEN takia jumala-NOM.PL minä-GEN kääntyä.pois-PRS.3PL" fi="Miksi sitten jumalat kääntyvät minusta pois?" />

<Note>
**Ristiriita.** Duolingon Tips & Notes ja *Verbal Prefixes* -sivu sanovat, että genetiivi on
mahdollinen vain, jos **verbi alkaa konsonantilla**; muuten objekti on datiivissa. Sanakirjan
artikkeleissa (esim. <Hv>upālegon</Hv>, <Hv>henumāzigon</Hv>) sanotaan vain ”datiivi, tai valinnaisesti
genetiivi, jos yksikkö on suoraan verbin edessä”. Rhaenyran <Hv fi="kääntyvät minusta pois">yno upālesi</Hv> on genetiivi
vokaalilla alkavan verbin edellä, mutta wikin jaksosivun alaviite merkitsee sen Petersonin virheeksi:
oikein olisi datiivi <Hv l="nyke">ynot</Hv>. Lause ei kumoa sääntöä.
Omissa kirjeissä datiivi on aina turvallinen valinta.
</Note>

Wiki lisää tärkeän tarkennuksen: verbin edessä ei saa olla muuta samassa sijassa olevaa sanaa kuin
applikatiivin objekti. Wikin esimerkissä *Ābro tala hembistas* ’naisen tytär lähti’ on oikein, mutta
*Tala ābro hembistas* tarkoittaisi ’tytär lähti naisesta’.

## Obliikvinen applikatiivi ja *syt-*

Obliikvinen applikatiivi *i-/j-* tekee datiivissa olevasta (tai muussa vinosijassa olevasta)
täydennyksestä akkusatiiviobjektin. Duolingosta tuttu on <Hv glossed>ilimagon</Hv> ’itkeä jonkun puolesta’.
Tässä muutama lisää:

- <Hv glossed>ivestragon</Hv> ’kertoa jollekulle’: <Hv>vestragon</Hv>-verbin kanssa kuulija on datiivissa,
  <Hv>ivestragon</Hv>-verbin kanssa akkusatiivissa. Wikin vertailu: <Hv glossed>Belmurtoti vestrās</Hv> → <Hv glossed>Belmurtī ivestrās</Hv> ’kerro orjakauppiaille’.

<Example id="ex-17fb52515c32" />

<Line id="got-s4e03-003" />

(<Hv glossed>Mērī jemī ivestran</Hv> ’puhun vain teille’.)

- <Hv glossed>iepagon</Hv> ’pyytää joltakulta’ (< <Hv>epagon</Hv>). Wikin mukaan Peterson kirjoittaa sen
  muodossa <Hv>iepagon</Hv>; nykyiset puhujat ääntävät usein <Hv>jepagon</Hv>.

Adpositio <Hv glossed>syt</Hv> ’jonkun puolesta’ voi siirtyä verbiin, ja silloin sen perään tulee
obliikvinen applikatiivi: <Hv glossed>Yno syt vīlībilāt?</Hv> → <Hv glossed>Yne sytivīlībilāt?</Hv> ’taisteletteko
minun puolestani?’. Samanlaisia ovat <Hv glossed>sytisindigon</Hv> ’ostaa jollekulle’, <Hv glossed>sytiotāpagon</Hv>
’neuvoa’ (kirjaimellisesti ’ajatella jonkun puolesta’) ja <Hv glossed>sytilībagon</Hv> ’kuulua jollekulle’.

### Idiomi: *iderēbagon*

<Hv glossed>Derēbagon</Hv> on ’kerätä’ ja <Hv glossed>iderēbagon</Hv> ’valita’. Wikin mukaan jälkimmäinen on
kirjaimellisesti ’kerätä jonkun hyväksi’: akkusatiivissa on se, **jolle** valitaan, ja valittu asia
on datiivissa.
Daenerys:

<Line id="got-s3e05-009" />

<Hv glossed>Mirre tolie iderēbās</Hv> ’valitse joku toinen (nimi)’.

## Muut etuliitteet

Valenssietuliitteiden lisäksi sanakirjassa on joukko johdinetuliitteitä. Wikin johdinsivu
(*Derivational Affixes*) kuvaa ne näin:

<div className="lesson-table-wrap" tabIndex={0}>
<table className="lesson-table">
  <thead><tr><th>Etuliite</th><th>Merkitys</th><th>Esimerkkejä</th></tr></thead>
  <tbody>
    <tr><td><Hv>jo-</Hv>, <Hv>jor-</Hv></td><td>jatkuvuus: ’yhä, edelleen’</td><td><Hv glossed>jorepagon</Hv> ’rukoilla’ (< pyytää), <Hv glossed>jorrāelagon</Hv> ’rakastaa’ (< pitää tallessa), <Hv glossed>joheghagon</Hv> ’teurastaa yhä’</td></tr>
    <tr><td><Hv>mī-</Hv>, <Hv>mīv-</Hv></td><td>tilapäisyys</td><td><Hv glossed>mīvojughagon</Hv> ’unohtaa’ (’hukata hetkeksi’), <Hv glossed>mīvindigon</Hv> ’kiusata’</td></tr>
    <tr><td><Hv>maz-</Hv></td><td>alkaminen (< <Hv glossed>māzigon</Hv> ’tulla’)</td><td><Hv glossed>mazverdagon</Hv> ’luoda’ (< järjestää), <Hv glossed>mazemagon</Hv> ’ottaa’ (< omistaa), <Hv glossed>mazōregon</Hv> ’ottaa vastaan’ (< pitää)</td></tr>
    <tr><td><Hv>nā-</Hv></td><td>vastakohta, kumoaminen</td><td><Hv glossed>nādīnagon</Hv> ’poistaa’ (< asettaa), <Hv glossed>nājikagon</Hv> ’viedä pois’, <Hv glossed>nātojagon</Hv> ’kaivaa esiin’</td></tr>
    <tr><td><Hv>qrin-</Hv>, <Hv>qrim-</Hv>, <Hv>qril-</Hv>, <Hv>qrīd-</Hv></td><td>huonosti, väärin (vrt. englannin <em>mis-</em>)</td><td><Hv glossed>qringaomagon</Hv> ’erehtyä’ (< tehdä), <Hv glossed>qrimpālegon</Hv> ’pettää’ (< kääntää), <Hv glossed>qrimbrōzagon</Hv> ’kirota’ (< nimetä)</td></tr>
    <tr><td><Hv>oz-</Hv>, <Hv>os-</Hv></td><td>tehostus</td><td><Hv glossed>ossēnagon</Hv> ’surmata’ (< tappaa), <Hv glossed>ohīlagon</Hv> ’puukottaa’ (< lyödä), <Hv glossed>ozūndegon</Hv> ’todistaa’</td></tr>
    <tr><td><Hv>ā-</Hv>, <Hv>ar-</Hv></td><td>toisto</td><td><Hv glossed>āmāzigon</Hv> ’palata’, <Hv glossed>āmazverdagon</Hv> ’luoda uudelleen’, <Hv glossed>āsikagon</Hv> ’synnyttää uudelleen’</td></tr>
  </tbody>
</table>
</div>

Sarjoista löytyy esimerkkejä kaikista:

<Example id="ex-5a505dfcead9" />

Melisandre pilkkaa Thorosia: *jo-* + <Hv>heghagon</Hv> ja *jo-* + <Hv glossed>mōzugon</Hv> – ’teurastavat
yhä’, ’juot yhä itsesi humalaan’.

<Line id="got-s8e06-005" />

<Line id="hotd-s3e08-003" />

<Hv glossed>Nātojās</Hv> ’kaiva esiin’ (*nā-* + ’haudata’) – Rhaenyra käskee kitkemään Hightowerit juuriaan
myöten.

<Example id="ex-62e752867160" />

<Hv glossed>Qringōntan</Hv> ’erehdyin’ – kirjaimellisesti ’tein väärin’: *qrin-* + <Hv>gaomagon</Hv>-verbin
perfektivartalo <Hv>gōnt-</Hv>.

<Line id="hotd-s3e03-008" />

Kaksi *qrin*-verbiä samassa repliikissä: <Hv glossed>qrindārlitas</Hv> ’on anastanut’ (vrt.
<Hv glossed>dārligon</Hv> ’ottaa haltuunsa’, <LessonLink n={39}>oppitunti 39</LessonLink>) ja <Hv glossed>qrimmajēptas</Hv> ’on soluttautunut’ (vrt.
<Hv glossed>majēbagon</Hv> ’astua sisään’).

<Line id="got-s6e08-001" />

<Hv glossed>Āmazverdagon āsittaks</Hv> – kaksi *ā*-etuliitettä peräkkäin: ’synnytettiin uudelleen luomaan
uudelleen’.

<Note>
**Ole varovainen omien johdosten kanssa.** Wikin mukaan etuliitteet ovat ”siirtyneet taivutuksesta
johtamisen puolelle”, ja monella johdoksella on idiomaattinen merkitys, jota ei voi arvata
(<Hv glossed>jorepagon</Hv> on ’rukoilla’, ei ’pyytää yhä’). Etuliitteiden tuntemisesta on apua ennen kaikkea
**lukiessa**. Omissa kirjeissä kannattaa käyttää sanakirjasta löytyviä verbejä. Jos keksitte oman,
ehdottakaa sitä omaksi sanaksi, niin se merkitään teidän keksimäksenne.
</Note>

## Harjoituslauseita

<Practice hv="Henujagon jaelan." fi="Haluan lähteä." />

<Practice hv="Ziry ivestrās." fi="Kerro hänelle." />

<Practice hv="Avy sytivīlībinna." fi="Taistelen puolestasi." />

<Practice hv="Sepār qringōntā." fi="Siis erehdyit." />

## Verbinjohtimet: kun verbit syntyvät verbeistä

Viimeinen verbioppitunti on palkinto suomenpuhujalle. Suomi on johtimien kieli: *kaatua → kaataa*,
*punainen → punastua*, *vapaa → vapauttaa*, *terve → tervehtiä*. Korkeavalyriassa on hämmästyttävän
samanlaisia johtimia, ja joskus vastaavuus on lähes sanasta sanaan. Oppitunnin lähteinä ovat wikin
johdinsivu (*Derivational Affixes*) ja kielioppisivun osio *Verb Derivation*.

Kuten etuliitteistä, johtimista on apua ennen kaikkea **lukiessa** ja sanoja
muistaessa. Monella johdoksella on idiomaattinen merkitys, joten omissa kirjeissä kannattaa käyttää
sanakirjan sanoja.

## *-emagon*: tehdä jostakin jonkinlainen

Faktitiivinen *-emagon* tekee adjektiivista (joskus verbistä) verbin ’tehdä jostakin X’. Se on
alkuperältään <Hv glossed>emagon</Hv> ’omistaa’, ja wikin mukaan johdokset perivät sen epäsäännöllisen
perfektin: **-ēdan**.

<div className="lesson-table-wrap" tabIndex={0}>
<table className="lesson-table">
  <thead><tr><th>Kantasana</th><th>Johdos</th><th>Suomeksi</th></tr></thead>
  <tbody>
    <tr><td><Hv glossed>lyka</Hv> ’hiljainen’</td><td><Hv glossed>lykemagon</Hv></td><td>hiljentää, rauhoittaa</td></tr>
    <tr><td><Hv glossed>dāez</Hv> ’vapaa’</td><td><Hv glossed>dāeremagon</Hv></td><td>vapauttaa</td></tr>
    <tr><td><Hv glossed>quba</Hv> ’matala’</td><td><Hv glossed>qubemagon</Hv></td><td>laskea, madaltaa</td></tr>
    <tr><td>juuri <Hv>vok-</Hv> ’puhdas’</td><td><Hv glossed>vōkemagon</Hv></td><td>puhdistaa</td></tr>
    <tr><td><Hv glossed>ūbrie</Hv> ’kypsä’</td><td><Hv glossed>ūbremagon</Hv></td><td>kasvattaa</td></tr>
    <tr><td><Hv glossed>adere</Hv> ’sileä, nopea’</td><td><Hv glossed>ademmagon</Hv></td><td>maksaa (wikin mukaan kirj. ’tasoittaa’)</td></tr>
  </tbody>
</table>
</div>

<Compare fi="vapa|uttaa" hv="dāer|emagon" label="faktitiivi: adjektiivista verbi" />

<Paradigm lemma="lykemagon" filter="indicative.active.perfect" />

Sarjoissa näitä on paljon. Aegon herättää lohikäärmettään:

<Line id="hotd-s3e04-001" />

<Hv glossed>Kiremās</Hv> ’herää / herätä’ (sanakirjassa <Hv glossed>kiremagon</Hv> ’herättää’). Punainen papitar
ennustaa:

<Line id="got-s5e03-008" />

Ja Daenerys viimeisellä kaudella – kolme *-emagon*-verbiä yhdessä puheessa:

<Line id="got-s8e06-004" />

<Hv glossed>Ūbrēdāks</Hv> ’teidät kasvatettiin’, <Hv glossed>dāerēdāt</Hv> ’olette vapauttaneet’, <Hv glossed>qubemiluty
daor</Hv> ’emme laske (keihäitämme)’. Huomaa perfektin *-ēd-*.

## *-ikagon*, *-kagon*: saada tekemään (kausatiivi)

Kausatiivinen johdin tekee verbistä verbin ’saada joku tekemään jotakin’ aivan kuten suomen *-tta-*.
Perfekti on **-ttan**.

<div className="lesson-table-wrap" tabIndex={0}>
<table className="lesson-table">
  <thead><tr><th>Kantaverbi</th><th>Kausatiivi</th><th>Suomen pari</th></tr></thead>
  <tbody>
    <tr><td><Hv glossed>ropagon</Hv> ’kaatua’</td><td><Hv glossed>ropakagon</Hv> ’kaataa’</td><td>kaatua → kaataa</td></tr>
    <tr><td><Hv glossed>jagon</Hv> ’mennä’</td><td><Hv glossed>jikagon</Hv> ’lähettää’</td><td>lähteä → lähettää</td></tr>
    <tr><td><Hv glossed>sagon</Hv> ’olla’</td><td><Hv glossed>sikagon</Hv> ’synnyttää’</td><td>syntyä → synnyttää</td></tr>
    <tr><td><Hv glossed>kostagon</Hv> ’voida’</td><td><Hv glossed>kustikagon</Hv> ’vahvistaa, rohkaista’</td><td>–</td></tr>
    <tr><td><Hv glossed>umbagon</Hv> ’odottaa’</td><td><Hv glossed>umbikagon</Hv> ’viivyttää’</td><td>odottaa → odotuttaa</td></tr>
  </tbody>
</table>
</div>

<Compare fi="kaa|taa" hv="ropa|kagon" label="kausatiivi" />

Pari *jagon* – *jikagon* on erityisen kaunis: suomen *lähettää* on itsekin johdettu verbistä
*lähteä* – ’saada lähtemään’. Samoin *synnyttää* ← *syntyä*, valyriassa taas *sikagon* ← *sagon*
’olla’ (’saada olemaan’).

<Paradigm lemma="jikagon" filter="indicative.active.perfect" />

Daenerys Tahrattomille:

<Line id="got-s3e04-009" />

<Hv glossed>Ōdrikātās</Hv> ’vahingoittakaa’ – wikin mukaan samaa juurta kuin <Hv glossed>ōdres</Hv> ’kipu’: ’aiheuttaa
kipua’. Ja Harmaalle Madolle:

<Line id="got-s4e03-002" />

<Hv glossed>Hinikagon</Hv> ’asettaa vaaraan’, kirjaimellisesti ’panna ulos’.

<Line id="hotd-s1e02-003" />

<Hv glossed>Umbitti</Hv> ’emme tahtoneet’ – <Hv glossed>umbikagon</Hv>-verbin toinen merkitys on ’olla haluton,
välttää tekemästä’.

## *-ūljagon* ja *-ñagon*: tulla joksikin

Inkoatiivit kertovat tilaan joutumisesta: ’tulla X:ksi’. Suomessa samaa tehtävää hoitavat johtimet
*-ntu-*, *-stu-* ja *-ttu-*: *kova → kovettua*, *punainen → punastua*.

<div className="lesson-table-wrap" tabIndex={0}>
<table className="lesson-table">
  <thead><tr><th>Kantasana</th><th>Johdos</th><th>Suomen vastine</th></tr></thead>
  <tbody>
    <tr><td><Hv glossed>lōta</Hv> ’kova’</td><td><Hv glossed>lōtūljagon</Hv></td><td>kovettua; varmistua</td></tr>
    <tr><td><Hv glossed>ūbrie</Hv> ’kypsä’</td><td><Hv glossed>ūbriljagon</Hv></td><td>kypsyä, kasvaa</td></tr>
    <tr><td><Hv glossed>giez</Hv> ’kokonainen’</td><td><Hv glossed>gierūljagon</Hv></td><td>kokoontua</td></tr>
    <tr><td><Hv glossed>morghe</Hv> ’kuollut’</td><td><Hv glossed>morghūljagon</Hv></td><td>kuolla (’tulla kuolleeksi’)</td></tr>
    <tr><td><Hv glossed>arlie</Hv> ’uusi’</td><td><Hv glossed>arliñagon</Hv></td><td>uudistua, muuttua</td></tr>
    <tr><td><Hv glossed>mele</Hv> ’punainen’</td><td><Hv glossed>melīñagon</Hv></td><td>punastua</td></tr>
    <tr><td><Hv glossed>sagon</Hv> ’olla’</td><td><Hv glossed>siñagon</Hv></td><td>tulla joksikin</td></tr>
  </tbody>
</table>
</div>

<Compare fi="punas|tua" hv="melī|ñagon" label="inkoatiivi" />

Kyllä – *Valar morghūlis* on kirjaimellisesti ”kaikki ihmiset tulevat kuolleiksi”.

Daemonin käsky:

<Line id="hotd-s1e08-002" />

<Hv glossed>Lōtūljās</Hv> ’varmista’ – sanakirjan mukaan <Hv glossed>lōtūljagon</Hv> on ’kovettua’ mutta myös
’varmistua, varmistaa’. Suomen *varmistaa* on johdettu sanasta *varma*, ja ajatus on täsmälleen
sama: kova = varma.

<Line id="hotd-s1e02-005" />

<Hv glossed>Giēnilus</Hv> ’parantuisi’ < <Hv glossed>giēñagon</Hv> ’parantua’ (wikin mukaan todennäköisesti
<Hv glossed>giez</Hv> ’kokonainen’ + *-ñagon* – ’tulla kokonaiseksi’; suomen *parantua* ← *parempi*).

## *-ākogon*: muuttua joksikin

Duolingo esitteli *-ākogon*-verbit ja niiden lokatiiviobjektin. Wikin johdinsivun mukaan merkitys
on ’muuttua X:ksi’, ja usein johdoksella on idiomaattinen merkitys: <Hv glossed>zoklākogon</Hv> ’houkutella’
(< <Hv glossed>zokla</Hv> ’susi’), <Hv glossed>jehākogon</Hv> ’ilahduttaa’. Esimerkki sarjasta:

<Line id="hotd-s1e04-009" />

<Hv glossed>Vapār zirȳ morghākotas</Hv> ’kunnes se tappoi hänet’ – objekti <Hv>zirȳ</Hv> on lokatiivissa.

## *-uragon*: käyttää jotakin

Wikin mukaan *-uragon* tekee substantiivista verbin ’käyttää X:ää’ (Peterson on tosin sanonut, ettei
se ole kovin yleinen johdin). Suomalaiselle osuvin vertailu:

<div className="lesson-table-wrap" tabIndex={0}>
<table className="lesson-table">
  <thead><tr><th>Kantasana</th><th>Johdos</th><th>Suomen pari</th></tr></thead>
  <tbody>
    <tr><td><Hv glossed>rytsas</Hv> ’terve!’</td><td><Hv glossed>rytsuragon</Hv> ’tervehtiä’</td><td>terve → tervehtiä</td></tr>
    <tr><td><Hv glossed>belmon</Hv> ’kahle’</td><td><Hv glossed>belmuragon</Hv> ’kahlita’</td><td>kahle → kahlita</td></tr>
    <tr><td><Hv glossed>ondos</Hv> ’käsi’</td><td><Hv glossed>onduragon</Hv> ’tarttua’</td><td>käsi → kätellä</td></tr>
    <tr><td><Hv glossed>deks</Hv> ’jalka’</td><td><Hv glossed>dekuragon</Hv> ’astua’</td><td>–</td></tr>
  </tbody>
</table>
</div>

<Compare fi="terveh|tiä" hv="ryts|uragon" label="’käyttää tervehdystä’" />

<Note>
**Sanan tarina.** Peterson teki sanan <Hv glossed>belmon</Hv> ’kahle’ Castlevania-pelien Simon Belmontin ja tämän ketjuruoskan kunniaksi. Wikissä hän toteaa toivovansa nyt, että sanan merkitys olisi jotain hyödyllisempää kuin ”kahle”. Tarinan nosti esiin jan Misali (Conlang Critic).
</Note>

Zanrush Meereenissä:

<Line id="got-s6e01-005" />

<Hv glossed>Ondurilāt</Hv> ’tartutteko’. Ja kirjeisiin aivan välttämätön tieto: <Hv glossed>rytsuragon</Hv> tarkoittaa
sanakirjan mukaan myös ’kiittää’ (instrumentaali <Hv l="kirimves">kirimvose</Hv> + akkusatiivi):

<Line id="got-s4e10-004" />

<Interlinear hv="Yne urn|iō syt kirimvose avy rytsur|an." gloss="minä-ACC nähdä-PRS.SBJV.2SG takia kiitos-INS sinä-ACC tervehtiä-PRS.1SG" fi="Kiitos, että otit minut vastaan." />

## *-egon*: tapahtuman tulos (eventatiivi)

Tällä johtimella ei ole suomessa vastinetta. Eventatiivi muodostetaan lisäämällä toisen verbin
**perfektivartaloon** *-egon*. Wikin mukaan merkitys ”keskittyy tapahtuman tulokseen”, mutta
tarkkaa merkitystä ei tiedetä. Esimerkit kuitenkin puhuvat puolestaan:

<div className="lesson-table-wrap" tabIndex={0}>
<table className="lesson-table">
  <thead><tr><th>Kantaverbi (perfektivartalo)</th><th>Eventatiivi</th></tr></thead>
  <tbody>
    <tr><td><Hv glossed>urnegon</Hv> ’nähdä’ (<Hv>ūnd-</Hv>)</td><td><Hv glossed>ūndegon</Hv> ’nähdä, havaita, katsoa’</td></tr>
    <tr><td><Hv glossed>rȳbagon</Hv> ’kuulla’ (<Hv>rypt-</Hv>)</td><td><Hv glossed>ryptegon</Hv> ’kuunnella’</td></tr>
    <tr><td><Hv glossed>sylugon</Hv> ’yrittää’ (<Hv>sylut-</Hv>)</td><td><Hv glossed>sylutegon</Hv> ’maistaa’</td></tr>
    <tr><td><Hv glossed>rhaenagon</Hv> ’tavata’ (<Hv>rhēd-</Hv>)</td><td><Hv glossed>rhēdegon</Hv> ’tuntea (henkilö)’</td></tr>
    <tr><td><Hv glossed>gaomagon</Hv> ’tehdä’ (<Hv>gōnt-</Hv>)</td><td><Hv glossed>gōntegon</Hv> ’saavuttaa’</td></tr>
    <tr><td><Hv glossed>sahagon</Hv> ’pakottaa’ (<Hv>sēt-</Hv>)</td><td><Hv glossed>sētegon</Hv> ’luoda, muovata’</td></tr>
    <tr><td><Hv glossed>kostagon</Hv> ’voida’ (<Hv>kōtt-</Hv>)</td><td><Hv glossed>kōttegon</Hv> ’osoittaa kykynsä’</td></tr>
  </tbody>
</table>
</div>

Kun tunnet perfektivartalot (<LessonLink n={43}>oppitunti 43</LessonLink>), tunnistat nämä verbit heti. Luke lohikäärmeelleen:

<Line id="hotd-s1e10-002" />

<Hv glossed>Ryptēs! Rȳbās!</Hv> ’Kuuntele! Tottele!’ – kantaverbi ja sen eventatiivi vierekkäin.
(<Hv glossed>Rȳbagon</Hv> tarkoittaa sanakirjan mukaan myös ’totella’.)

<Line id="hotd-s3e03-013" />

<Line id="got-s3e06-003" />

## *-ligon* ja *ā-*: uudelleen

Toistoa ilmaisee joko etuliite *ā-* (edellä, ”Muut etuliitteet”) tai johdin *-ligon* (perfekti *-litan*). Wikissä
ei kerrota, miten ne eroavat merkitykseltään. Punainen papitar:

<Line id="got-s5e03-006" />

<Hv glossed>Udlitas</Hv> ’on vastannut’ (<Hv glossed>udligon</Hv> ’vastata’ – kirjaimellisesti ehkä ’puhua takaisin’),
<Hv glossed>siglitaks</Hv> ’hänet synnytettiin uudelleen’ (< <Hv>sikagon</Hv>), <Hv glossed>verdlios</Hv> ’jotta hän
loisi uudelleen’ (< <Hv glossed>verdagon</Hv> ’järjestää’ – ja tarkoituksen subjunktiivi <LessonLink n={39}>oppitunnilta 39</LessonLink>).

## Lopuksi: *-ēbagon* ja muut ”tyhjät” johtimet

Sanakirjassa on paljon verbejä, joiden lopussa on *-ēbagon, -ībagon, -ūbagon, -ābagon* tai
vastaava. Wikin mukaan johtimella ei luultavasti ole omaa merkitystä, vaan sillä muodostetaan
kantaverbin merkitystä lähellä olevia verbejä: <Hv glossed>urnegon</Hv> ’nähdä’ → <Hv glossed>urnēbagon</Hv> ’katsella, tuijottaa’,
<Hv glossed>dekuragon</Hv> ’astua’ → <Hv glossed>dekurūbagon</Hv> ’kävellä’. Lukiessa riittää tunnistaa juuri.

## Yhteenveto

<div className="lesson-table-wrap" tabIndex={0}>
<table className="lesson-table">
  <thead><tr><th>Johdin</th><th>Merkitys</th><th>Suomen vastine</th></tr></thead>
  <tbody>
    <tr><td><Hv>-emagon</Hv></td><td>tehdä jostakin X</td><td><em>-ista-, -(u)tta-</em> (puhdistaa, vapauttaa)</td></tr>
    <tr><td><Hv>-ikagon</Hv>, <Hv>-kagon</Hv></td><td>saada tekemään</td><td><em>-tta-</em> (kaataa, lähettää)</td></tr>
    <tr><td><Hv>-ūljagon</Hv>, <Hv>-iljagon</Hv>, <Hv>-ñagon</Hv></td><td>tulla X:ksi</td><td><em>-ntu-, -stu-, -ttu-</em> (huonontua, punastua, kovettua)</td></tr>
    <tr><td><Hv>-ākogon</Hv></td><td>muuttua X:ksi</td><td><em>-ksi + tulla</em></td></tr>
    <tr><td><Hv>-uragon</Hv></td><td>käyttää X:ää</td><td><em>-ita-, -htiä</em> (kahlita, tervehtiä)</td></tr>
    <tr><td><Hv>-egon</Hv> (perfektivartalosta)</td><td>tapahtuman tulos</td><td>ei vastinetta</td></tr>
    <tr><td><Hv>-ligon</Hv>, <Hv>ā-</Hv></td><td>uudelleen</td><td><em>uudelleen, taas</em></td></tr>
  </tbody>
</table>
</div>

## Harjoituslauseita

<Practice hv="Avy rytsuran." fi="Tervehdin sinua." />

<Practice hv="Rūniapos aōt jittan." fi="Lähetin sinulle kirjeen." />

<Practice hv="Riñi ūbrilisi." fi="Tytöt kasvavat (kuten tytöt aina)." />

<Practice hv="Ryptēs!" fi="Kuuntele!" />

## Sanat

<Vocab />

<Check />
